// ISO 8601 week helpers (same rules as lanewerk-core/src/calendar.rs).
import type { ColKind } from "./engine-api";

export function isoWeek(d: Date): { year: number; week: number } {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return { year: t.getUTCFullYear(), week: Math.ceil(((t.getTime() - yearStart.getTime()) / 864e5 + 1) / 7) };
}

/** Monday of an ISO week (UTC date). */
export function mondayOf(year: number, week: number): Date {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const day = jan4.getUTCDay() || 7;
  return new Date(Date.UTC(year, 0, 4 - day + 1 + (week - 1) * 7));
}

const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
export function weekRange(year: number, week: number): string {
  const m = mondayOf(year, week);
  const s = new Date(m.getTime() + 6 * 864e5);
  return `${m.getUTCDate()}. ${MONTHS[m.getUTCMonth()]} – ${s.getUTCDate()}. ${MONTHS[s.getUTCMonth()]}`;
}

/** "2026-01-15" → "15.01." */
export function shortDate(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}.${m}.`;
}

/** Local calendar date as "YYYY-MM-DD" (not UTC: "today" is the viewer's today). */
export function localDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Calendar weeks touched by a month, e.g. "KW 44 – 48" (masterplan sub-header). */
export function monthWeeks(year: number, month: number): string {
  const first = isoWeek(new Date(year, month - 1, 1)).week;
  const last = isoWeek(new Date(year, month, 0)).week;
  return `KW ${first} – ${last}`;
}

/**
 * Where the day `d` (local calendar day) lies in a week or month column, as a fraction of the column
 * width (middle of the day: Monday of a week → 1/14); null when the column is another week or month.
 */
export function dayIn(kind: ColKind, d: Date): number | null {
  if (kind.kind === "week") {
    const w = isoWeek(d);
    if (w.year !== kind.value.year || w.week !== kind.value.week) return null;
    return (((d.getDay() + 6) % 7) + 0.5) / 7;
  }
  if (kind.kind === "month") {
    if (d.getFullYear() !== kind.value.year || d.getMonth() + 1 !== kind.value.month) return null;
    return (d.getDate() - 0.5) / new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  }
  return null;
}

const POINTS = new Intl.NumberFormat("de-CH", { maximumFractionDigits: 1 });
/** Story points for display: "3", "2.5" (de-CH). */
export function formatPoints(p: number): string {
  return POINTS.format(p);
}

/** "2026-W43" (ISO notation, as the engine and the server write weeks). */
export const weekKey = (year: number, week: number) => `${year}-W${String(week).padStart(2, "0")}`;

/** "KW 43 – 46", one week: "KW 43" (section tabs). */
export function weekSpan(a: { n: number }, b: { n: number }): string {
  return a.n === b.n ? `KW ${a.n}` : `KW ${a.n} – ${b.n}`;
}

/** "Okt – Dez 2026", "Dez 2026 – Feb 2027", one month: "Dez 2026" (section tabs). */
export function monthSpan(a: { year: number; n: number }, b: { year: number; n: number }): string {
  const m = (v: { n: number }) => MONTHS[v.n - 1];
  if (a.year === b.year && a.n === b.n) return `${m(a)} ${a.year}`;
  return a.year === b.year ? `${m(a)} – ${m(b)} ${b.year}` : `${m(a)} ${a.year} – ${m(b)} ${b.year}`;
}

/**
 * The part of a month column covered by the ISO week holding `d` (local calendar days), as
 * fractions of the column width; null when that week lies outside the month.
 */
export function weekInMonth(kind: ColKind, d: Date): [number, number] | null {
  if (kind.kind !== "month") return null;
  const { year, month } = kind.value;
  const days = new Date(year, month, 0).getDate();
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  const first = new Date(year, month - 1, 1), last = new Date(year, month - 1, days);
  if (sunday < first || monday > last) return null;
  return [monday < first ? 0 : (monday.getDate() - 1) / days, sunday > last ? 1 : sunday.getDate() / days];
}
